import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import { ReactComponent as LiveCameraHoverIcon } from 'src/Images/icons/common/LiveCameraHoverIcon.svg';
import { ReactComponent as LiveCameraCloseIcon } from 'src/Images/icons/common/LiveCameraCloseIcon.svg';
import { ReactComponent as ArrowLeftIcon } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRightIcon } from 'src/Images/icons/common/arrowright.svg';
import getColorCode from 'src/utils/GetThemeColor';
import { getWeighBridgeWorkCenters } from 'src/redux/Actions/VehicleTracking';

const SELECTED_PORT_LOCAL_STORAGE_KEY = 'vehicleTrackingLastSerialPortIndex';
const READ_INTERVAL_MS = 5000; // how often the weight on screen is refreshed
const MAX_READ_DURATION_MS = 60 * 60 * 1000; // stop reading after 1 hour

// Shared by every visit to this screen, so a new visit waits until the
// previous one has finished closing the port
let serialQueue = Promise.resolve();

function LiveFeedFromCameraTile({ camera, expanded = false, onClick }) {
  return (
    <button
      type="button"
      className={`live-screen-camera-tile ${expanded ? 'expanded' : ''}`}
      onClick={onClick}
      aria-label={`Open ${camera.name}`}
    >
      <iframe
        src={`${camera.url}?autoplay=true&muted=true&controls=false`}
        title={camera.name}
        className="live-screen-camera-media"
        allow="autoplay; fullscreen"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />

      {!expanded && (
        <span
          className="live-screen-camera-expand-icon"
          role="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={(event) => {
            event.stopPropagation();
            onClick();
          }}
        >
          <LiveCameraHoverIcon />
        </span>
      )}
    </button>
  );
}

export default function VehicleTrackingLiveScreen() {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );

  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer.userData.tenant?.theme;
  const allWorkCenters = vehicleTrackingReducer?.weighBridgeWorkCenters || [];

  const weighBridgeWorkCenters = useMemo(
    () => allWorkCenters.filter((wc) => wc?.work_center_type?.id === 1),
    [allWorkCenters],
  );
  const cameraWorkCenters = useMemo(
    () => allWorkCenters.filter((wc) => wc?.work_center_type?.id === 2),
    [allWorkCenters],
  );
  const weightBridgeOptions = useMemo(
    () =>
      weighBridgeWorkCenters.map((option) => ({
        value: option.id,
        label: option.name,
        ...option,
      })),
    [weighBridgeWorkCenters],
  );
  const cameraOptions = useMemo(
    () =>
      cameraWorkCenters.flatMap((workCenter) => {
        let extra = workCenter?.extra_field;

        if (typeof extra === 'string') {
          const trimmed = extra.trim();
          if (!trimmed) return [];
          try {
            extra = JSON.parse(trimmed);
          } catch {
            return [];
          }
        }
        if (typeof extra !== 'object' || extra === null) return [];

        const { camera_urls: cameraUrls, camera_ips: cameraIps } = extra;
        if (
          !Array.isArray(cameraUrls) ||
          cameraUrls.length === 0 ||
          !Array.isArray(cameraIps) ||
          cameraIps.length === 0
        ) {
          return [];
        }

        const liveFeedUrl = cameraUrls[0].replace(/\/+$/, '');
        return cameraIps.filter(Boolean).map((cameraPath, index) => ({
          id: `${workCenter.id}-${index}`,
          name: `${workCenter.name} ${index + 1}`,
          url: `${liveFeedUrl}/${cameraPath}`,
          ip: cameraPath,
        }));
      }),
    [cameraWorkCenters],
  );
  const scaleTickIds = useMemo(
    () => Array.from({ length: 80 }, (_, index) => `scale-tick-${index}`),
    [],
  );

  const storedPortIndexRef = useRef(
    (() => {
      try {
        const stored = localStorage.getItem(SELECTED_PORT_LOCAL_STORAGE_KEY);
        if (stored === null) return null;
        const parsed = Number(stored);
        return Number.isInteger(parsed) ? parsed : null;
      } catch {
        return null;
      }
    })(),
  );
  const cameraGridRef = useRef(null);

  const [selectedWeighBridge, setSelectedWeightBridge] = useState(null);
  const [rawWeighBridgeReading, setRawWeighBridgeReading] = useState('');
  const [expandedCameraId, setExpandedCameraId] = useState(null);
  const [availablePorts, setAvailablePorts] = useState(null);
  const [userSelectedPortIndex, setUserSelectedPortIndex] = useState(null);
  const expandedCamera = cameraOptions.find(
    (camera) => camera.id === expandedCameraId,
  );
  const ports = availablePorts ?? [];

  const portOptions = [
    ...ports.map((_, index) => ({
      type: 'port',
      index,
      value: String(index),
      label: `Port ${index + 1}`,
    })),
    { type: 'add-new', value: 'add-new', label: '+ Add new port...' },
  ];

  // User's pick, else the only port available, else the last used port
  const selectedPortIndex =
    userSelectedPortIndex ??
    (ports.length === 1 ? 0 : storedPortIndexRef.current);
  const selectedPort = ports[selectedPortIndex] ?? null;
  const selectedPortOption = selectedPort
    ? portOptions[selectedPortIndex]
    : null;

  const weightValueFromWeighBridge = rawWeighBridgeReading
    .trim()
    .match(/\)\s*0\s+(\d+(?:\.\d+)?)/);
  const displayWeight = weightValueFromWeighBridge?.[1] || '0';

  const handleCameraScroll = useCallback((direction) => {
    const el = cameraGridRef.current;
    if (!el) return;
    const firstTile = el.firstElementChild;
    const tileWidth = firstTile ? firstTile.getBoundingClientRect().width : 260;
    const gap = 14;
    el.scrollBy({ left: direction * (tileWidth + gap), behavior: 'smooth' });
  }, []);

  useEffect(() => {
    dispatch(getWeighBridgeWorkCenters(tenantOrganisationId, 1));
    dispatch(getWeighBridgeWorkCenters(tenantOrganisationId, 2));
  }, [dispatch, tenantOrganisationId]);

  useEffect(() => {
    if (!selectedWeighBridge && weightBridgeOptions.length > 0) {
      setSelectedWeightBridge(weightBridgeOptions[0]);
    }
  }, [selectedWeighBridge, weightBridgeOptions]);

  const getAvailablePortsForTheComputer = useCallback(async () => {
    if (!('serial' in navigator)) {
      setAvailablePorts([]);
      return [];
    }
    try {
      const nextPorts = await navigator.serial.getPorts();
      setAvailablePorts(nextPorts);
      return nextPorts;
    } catch {
      setAvailablePorts([]);
      return [];
    }
  }, []);

  useEffect(() => {
    getAvailablePortsForTheComputer();
  }, [getAvailablePortsForTheComputer]);

  // Reads the scale while a weigh bridge and a port are selected.
  // The port is read in the background, the screen refreshes every
  // READ_INTERVAL_MS, and everything stops after MAX_READ_DURATION_MS.

  useEffect(() => {
    setRawWeighBridgeReading('');
    if (!selectedWeighBridge || !selectedPort) return undefined;

    let isStopped = false,
      portReader = null,
      mostRecentWeightLine = '',
      hasDisplayedInitialReading = false;
    const readSessionStartedAt = Date.now();

    const stopReading = () => {
      isStopped = true;
      portReader?.cancel().catch(() => {}); // makes reader.read() return { done: true }
    };

    // Reads one stream until it ends ('ended') or fails ('failed').
    // Each read schedules the next one from its own callback, so nothing
    // piles up however long the port stays open.
    const readWeightStream = (activePortReader) =>
      new Promise((resolve) => {
        const textDecoder = new TextDecoder();
        let incompleteLineBuffer = '';

        const readNextChunk = () => {
          activePortReader
            .read()
            .then(({ value: rawBytes, done: streamEnded }) => {
              if (streamEnded || isStopped) {
                resolve('ended');
                return;
              }

              incompleteLineBuffer += textDecoder.decode(rawBytes, {
                stream: true,
              });
              const linesSplitFromBuffer =
                incompleteLineBuffer.split(/\r\n|\r|\n/);
              incompleteLineBuffer = linesSplitFromBuffer.pop(); // unfinished line waits for the next chunk
              const latestCompleteLine = linesSplitFromBuffer
                .map((lineText) => lineText.trim())
                .filter(Boolean)
                .pop();

              if (latestCompleteLine) {
                mostRecentWeightLine = latestCompleteLine;
                if (!hasDisplayedInitialReading) {
                  // show the first reading right away instead of waiting for the timer
                  hasDisplayedInitialReading = true;
                  setRawWeighBridgeReading(latestCompleteLine);
                }
              }
              readNextChunk();
            })
            .catch(() => resolve('failed'));
        };

        readNextChunk();
      });

    const readLoop = async () => {
      if (isStopped) return;
      try {
        if (!selectedPort.readable) {
          // 8 data bits, 1 stop bit, no parity are the defaults
          await selectedPort.open({ baudRate: 9600 });
        }
        portReader = selectedPort.readable.getReader();
        await readWeightStream(portReader);
        portReader.releaseLock();
      } catch {
        // port busy or unplugged: no lines arrive, so "No data received" shows
      } finally {
        await selectedPort.close().catch(() => {});
      }
    };

    // every READ_INTERVAL_MS: show the newest line, or '' if nothing arrived
    const displayRefreshTimer = setInterval(() => {
      if (Date.now() - readSessionStartedAt >= MAX_READ_DURATION_MS) {
        clearInterval(displayRefreshTimer);
        stopReading();
        setRawWeighBridgeReading('');
        return;
      }
      setRawWeighBridgeReading(mostRecentWeightLine);
      mostRecentWeightLine = '';
    }, READ_INTERVAL_MS);

    // wait for the previous read to finish closing the port before opening it
    serialQueue = serialQueue.then(readLoop).catch(() => {});

    return () => {
      clearInterval(displayRefreshTimer);
      stopReading();
    };
  }, [selectedWeighBridge, selectedPort]);

  const handlePortOptionChange = useCallback(
    async (selectedOption) => {
      if (!selectedOption) {
        setUserSelectedPortIndex(null);
        return;
      }

      if (selectedOption.type === 'add-new') {
        if (!('serial' in navigator)) return;
        try {
          await navigator.serial.requestPort();
          await getAvailablePortsForTheComputer();
        } catch {
          // user cancelled the picker or it failed
        }
        return;
      }

      if (selectedOption.type === 'port') {
        setUserSelectedPortIndex(selectedOption.index);

        // remember this port for the next visit
        storedPortIndexRef.current = selectedOption.index;
        try {
          localStorage.setItem(
            SELECTED_PORT_LOCAL_STORAGE_KEY,
            String(selectedOption.index),
          );
        } catch {
          // ignore — storage may be unavailable (private mode, etc.)
        }
      }
    },
    [getAvailablePortsForTheComputer],
  );

  return (
    <div className="live-screen-container">
      <div className="live-screen-stage-panel">
        <div className="live-screen-section-heading">
          <span className="text-md text-medium-bold">Camera</span>
        </div>

        <div className="live-screen-camera-section">
          {cameraOptions.length > 2 && (
            <button
              type="button"
              className="live-screen-camera-scroll-btn"
              onClick={() => handleCameraScroll(-1)}
              aria-label="Scroll cameraOptions left"
            >
              <ArrowLeftIcon
                className="icon-size-sm"
                color={getColorCode(themeColor)}
              />
            </button>
          )}

          <div className="live-screen-camera-grid" ref={cameraGridRef}>
            {cameraOptions.length > 0 ? (
              cameraOptions.map((camera) => (
                <LiveFeedFromCameraTile
                  key={camera.id}
                  camera={camera}
                  onClick={() => setExpandedCameraId(camera.id)}
                />
              ))
            ) : (
              <div className="live-screen-empty text-md text-grey">
                No camera streams configured.
              </div>
            )}
          </div>

          {cameraOptions.length > 2 && (
            <button
              type="button"
              className="live-screen-camera-scroll-btn"
              onClick={() => handleCameraScroll(1)}
              aria-label="Scroll cameraOptions right"
            >
              <ArrowRightIcon
                className="icon-size-sm"
                color={getColorCode(themeColor)}
              />
            </button>
          )}
        </div>

        <div className="live-screen-section-heading live-screen-weight-bridge-heading">
          <span className="text-md text-medium-bold">Weight Bridge</span>

          <div className="live-screen-bridge-heading-right">
            {weightBridgeOptions.length > 1 && (
              <div className="live-screen-bridge-section">
                <div className="input-wrapper live-screen-bridge-select">
                  <CustomMultiSelect
                    isMulti={false}
                    options={weightBridgeOptions}
                    value={selectedWeighBridge}
                    onChange={setSelectedWeightBridge}
                    placeholder="Select Weight Bridge"
                    isSearchable
                    className="multi-select-input-section"
                    classNamePrefix="react-select"
                  />
                  <span className="form-input-section-icon">
                    <DropdownIcon
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </span>
                </div>
              </div>
            )}

            <div className="live-screen-bridge-section">
              <div className="input-wrapper live-screen-comport-select">
                <CustomMultiSelect
                  isMulti={false}
                  options={portOptions}
                  value={selectedPortOption}
                  onChange={handlePortOptionChange}
                  placeholder={
                    availablePorts === null
                      ? 'Loading ports...'
                      : 'Select COM Port'
                  }
                  isDisabled={availablePorts === null}
                  isSearchable={false}
                  className="multi-select-input-section"
                  classNamePrefix="react-select"
                />
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>

        {selectedWeighBridge && (
          <>
            <div className="live-screen-weight-readout">
              <span>{displayWeight}</span>
              <span>kg</span>
            </div>

            <div className="live-screen-scale-track" aria-hidden="true">
              {scaleTickIds.map((tickId) => (
                <span key={tickId} />
              ))}
            </div>
          </>
        )}

        {availablePorts?.length === 0 && (
          <span className="live-screen-serial-error text-sm text-red">
            No port granted yet. Choose &quot;+ Add new port...&quot; to pick
            the weight bridge port.
          </span>
        )}

        {selectedPort && selectedWeighBridge && !rawWeighBridgeReading && (
          <span className="live-screen-serial-error text-sm text-red">
            No data received from the weight bridge. Check the connection.
          </span>
        )}
      </div>

      <Modal
        show={Boolean(expandedCamera)}
        onHide={() => setExpandedCameraId(null)}
        centered
        size="xl"
        dialogClassName="live-screen-camera-modal"
      >
        <Modal.Body className="live-screen-camera-modal-body">
          {expandedCamera && (
            <>
              <LiveFeedFromCameraTile
                camera={expandedCamera}
                expanded
                onClick={() => {}}
              />
              <button
                type="button"
                className="live-screen-camera-close"
                onClick={() => setExpandedCameraId(null)}
                aria-label="Close camera"
              >
                <LiveCameraCloseIcon className="icon-size-xxxl" />
              </button>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
